/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SortingState,
  IntegratedSorting,
  DataTypeProvider,
  SummaryState,
  IntegratedSummary,
  SearchState,
  IntegratedFiltering,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableHeaderRow,
  Toolbar,
  VirtualTable,
  ExportPanel,
  TableColumnVisibility,
  ColumnChooser,
  TableSummaryRow,
  SearchPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { getRowId, updateDocNumbers } from '../../common';
import {
  covertToTimeDateDigit,
  currencyFormatter,
  moneyFormat,
  opTypeFormatter,
  timeFormatter,
} from '../../Shared/colorFormat';
import { Box, fade, Typography, withStyles } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { GridExporter } from '@devexpress/dx-react-grid-export';
import saveAs from 'file-saver';
import { getColumns } from '../../common/columns';
import FilterSelectCkeckBox from '../../Shared/FilterSelectCkeckBox';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useEmployees from '../../hooks/useEmployees';
import useAccounts from '../../hooks/useAccounts';
import { SearchTable } from '../../components';
import getEmployeeAdvance from '../../graphql/query/getEmployeeAdvance';

const styles = (theme: any) => ({
  tableStriped: {
    '& tbody tr:nth-of-type(odd)': {
      backgroundColor: fade(theme.palette.primary.main, 0.05),
    },
  },
});

const TableComponentBase = ({ classes, ...restProps }: any) => (
  <VirtualTable.Table {...restProps} className={classes.tableStriped} />
);
const TableComponent = withStyles(styles, { name: 'TableComponent' })(
  TableComponentBase,
);

export default function AdvanceReport({
  isRTL,
  words,
  start,
  end,
  company,
  theme,
  drawerWidth,
}: any) {
  const [rows, setRows] = useState<any>([]);
  const [emplvalue, setEmplvalue] = useState<any>(null);

  const col = getColumns({ isRTL, words });

  const [columns] = useState([
    { name: 'time', title: words?.time },
    col.opType,
    { name: 'docNo', title: words.no },
    col.employee,
    { name: 'desc', title: words.description },
    { name: 'amount', title: words.amount },
  ]);

  const [tableColumnVisibilityColumnExtensions] = useState([
    { columnName: 'time', togglingEnabled: false },
    { columnName: 'amount', togglingEnabled: false },
  ]);

  const [loadAdvances, advanceData]: any = useLazyQuery(getEmployeeAdvance, {
    fetchPolicy: 'cache-and-network',
  });

  const { employees } = useEmployees();
  const { accounts } = useAccounts();
  const { height } = useWindowDimensions();

  useEffect(() => {
    if (start && end && emplvalue) {
      const variables = {
        employeeId: emplvalue._id,
        start: start ? start.setHours(0, 0, 0, 0) : undefined,
        end: end ? end.setHours(23, 59, 59, 999) : undefined,
      };
      loadAdvances({ variables });
    }
  }, [start, end, emplvalue]);

  useEffect(() => {
    if (advanceData?.data?.getEmployeeAdvance?.data) {
      const { data } = advanceData.data.getEmployeeAdvance;
      const rdata = updateDocNumbers(data);
      setRows(rdata);
    } else {
      setRows([]);
    }
  }, [advanceData]);

  const totalPaid = rows
    ?.filter((r: any) => r.opType === 78)
    ?.reduce((sum: any, r: any) => sum + (r.amount || 0), 0);
  const totalReceived = rows
    ?.filter((r: any) => r.opType === 77)
    ?.reduce((sum: any, r: any) => sum + (r.amount || 0), 0);
  const balance = totalPaid - totalReceived;

  const exporterRef: any = useRef(null);

  const startExport = useCallback(() => {
    exporterRef.current.exportGrid();
  }, [exporterRef]);

  const onSave = (workbook: any) => {
    workbook.xlsx.writeBuffer().then((buffer: any) => {
      const now = new Date();
      const name = `advance-report-${covertToTimeDateDigit(now)}`;
      saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `${name}.xlsx`,
      );
    });
  };

  const totalSummaryItems = [{ columnName: 'amount', type: 'sum' }];

  return (
    <>
      <Box
        display="flex"
        style={{
          position: 'absolute',
          top: 60,
          left: 450,
          zIndex: 112,
        }}
      >
        <FilterSelectCkeckBox
          options={employees}
          value={emplvalue}
          setValue={setEmplvalue}
          words={words}
          isRTL={isRTL}
          name="employee"
          nomulti
          width={250}
        ></FilterSelectCkeckBox>
      </Box>

      {emplvalue && rows?.length > 0 && (
        <Box
          style={{
            position: 'absolute',
            zIndex: 111,
            left: isRTL ? undefined : drawerWidth + 225,
            right: isRTL ? drawerWidth + 225 : undefined,
            bottom: 2,
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 20,
          }}
        >
          <Typography style={{ fontWeight: 'bold', color: '#c62828' }}>
            {isRTL ? 'إجمالي السلف' : 'Total Advances'}:{' '}
            {moneyFormat(totalPaid)}
          </Typography>
          <Typography style={{ fontWeight: 'bold', color: '#2e7d32' }}>
            {isRTL ? 'إجمالي السداد' : 'Total Repaid'}:{' '}
            {moneyFormat(totalReceived)}
          </Typography>
          <Typography style={{ fontWeight: 'bold', color: '#403795' }}>
            {isRTL ? 'الرصيد المتبقي' : 'Remaining Balance'}:{' '}
            {moneyFormat(balance)}
          </Typography>
        </Box>
      )}

      <Grid rows={rows} columns={columns} getRowId={getRowId}>
        <SortingState />
        <SummaryState totalItems={totalSummaryItems} />
        <IntegratedSorting />
        <IntegratedSummary />
        <SearchState />
        <IntegratedFiltering />
        <VirtualTable
          height={height - 100}
          tableComponent={TableComponent}
          messages={{
            noData: isRTL ? 'لا يوجد بيانات' : 'no data',
          }}
          estimatedRowHeight={40}
        />
        <TableHeaderRow
          showSortingControls
          titleComponent={({ children }) => {
            return (
              <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
                {children}
              </Typography>
            );
          }}
        />
        <TableColumnVisibility
          columnExtensions={tableColumnVisibilityColumnExtensions}
          defaultHiddenColumnNames={[]}
        />
        <DataTypeProvider
          for={['time']}
          formatterComponent={timeFormatter}
        ></DataTypeProvider>
        <DataTypeProvider
          for={['amount']}
          formatterComponent={currencyFormatter}
        ></DataTypeProvider>
        <DataTypeProvider
          for={['opType']}
          formatterComponent={opTypeFormatter}
        ></DataTypeProvider>
        <Toolbar
          rootComponent={(props: any) => (
            <Toolbar.Root
              style={{
                position: 'absolute',
                left: isRTL ? 40 : undefined,
                right: isRTL ? undefined : 40,
                top: 62,
                zIndex: 115,
              }}
              {...props}
            ></Toolbar.Root>
          )}
        />
        <ColumnChooser />
        <SearchPanel
          inputComponent={(props: any) => {
            return <SearchTable isRTL={isRTL} {...props}></SearchTable>;
          }}
        />
        <ExportPanel startExport={startExport} />
        <TableSummaryRow
          messages={{
            sum: isRTL ? 'المجموع' : 'Total',
            count: isRTL ? 'العدد' : 'Count',
          }}
        ></TableSummaryRow>
      </Grid>
      <GridExporter
        ref={exporterRef}
        rows={rows}
        columns={columns}
        onSave={onSave}
      />
    </>
  );
}
